import { useMemo } from 'react';

import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Button, useToggle } from '@commenda/kakashi';
import { TaskParty } from '@commenda/prisma';

import { SelectionActionBar } from '../../../components/SelectionActionBar';
import { IndirectTaxFilingsBulkApproveModal } from '../detail/IndirectTaxFilingsBulkApproveModal';
import { IndirectTaxFilingsBulkReviewModal } from '../detail/IndirectTaxFilingsBulkReviewModal';
import { IIndirectTaxFiling } from '../types';
import { IndirectTaxFilingsApprovalMailModal } from './IndirectTaxFilingsApprovalMailModal';

interface IndirectTaxFilingsBulkActionBarProps {
  selectedFilings: IIndirectTaxFiling[];
  corporationId: number;
  taskAssigneeType: TaskParty;
  onClear: () => void;
  onApprove?: () => void;
  onReview: () => void;
  onApprovalMailSent: (skippedCount: number) => void;
  onApprovalMailError: (message: string) => void;
}

const IndirectTaxFilingsBulkActionBar = ({
  selectedFilings,
  corporationId,
  taskAssigneeType,
  onClear,
  onApprove,
  onReview,
  onApprovalMailSent,
  onApprovalMailError,
}: IndirectTaxFilingsBulkActionBarProps) => {
  const approveModal = useToggle();
  const reviewModal = useToggle();
  const approvalMailModal = useToggle();

  const approvableIds = useMemo(
    () => selectedFilings.filter((f) => f.canApprove).map((f) => f.id),
    [selectedFilings]
  );

  const hasReviewableFiling = useMemo(
    () => selectedFilings.some((f) => f.canReview),
    [selectedFilings]
  );

  const selectedFilingIds = useMemo(
    () => selectedFilings.filter((f) => f.canReview).map((f) => f.id),
    [selectedFilings]
  );

  const isAgent = taskAssigneeType === TaskParty.AGENT;

  const approveLabel = approvableIds.length > 1 ? 'filings' : 'filing';
  const canSendApprovalMail =
    isFeatureLaunched(FeatureLaunch.INDIRECT_TAX_FILING_APPROVAL_REQUEST) &&
    approvableIds.length > 0;

  return (
    <>
      <SelectionActionBar
        count={selectedFilings.length}
        itemNoun="filing"
        onClear={onClear}
      >
        {onApprove && approvableIds.length > 0 && (
          <Button size="sm" variant="primary" onClick={approveModal.show}>
            <span className="txt-small-medium">
              Approve {approvableIds.length} {approveLabel}
            </span>
          </Button>
        )}
        {isAgent && hasReviewableFiling && (
          <Button size="sm" variant="primary" onClick={reviewModal.show}>
            <span className="txt-small-medium">Mark as reviewed</span>
          </Button>
        )}
        {canSendApprovalMail && (
          <Button
            size="sm"
            variant="secondary"
            onClick={approvalMailModal.show}
          >
            <span className="txt-small-medium">Send approval request</span>
          </Button>
        )}
      </SelectionActionBar>

      <IndirectTaxFilingsBulkApproveModal
        isOpen={approveModal.visible}
        onClose={approveModal.hide}
        filingIds={approvableIds}
        corporationId={corporationId}
        onApproved={onApprove}
      />

      <IndirectTaxFilingsBulkReviewModal
        isOpen={reviewModal.visible}
        onClose={reviewModal.hide}
        filingIds={selectedFilingIds}
        corporationId={corporationId}
        onReviewed={onReview}
      />

      <IndirectTaxFilingsApprovalMailModal
        isOpen={approvalMailModal.visible}
        onClose={approvalMailModal.hide}
        corporationId={corporationId}
        filingIds={approvableIds}
        onSent={onApprovalMailSent}
        onError={onApprovalMailError}
      />
    </>
  );
};

export { IndirectTaxFilingsBulkActionBar };
